import { gql, useQuery } from '@apollo/client';
import { AlertBanner, Button } from '@apollo/orbit';
import React from 'react';
import { Link } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import type { GraphQLTypes } from 'src/lib/graphqlTypes';
import { BillingProvider } from 'src/lib/graphqlTypes/types';

import { PermissionGuard } from '../permissionGuards/permissionGuard/PermissionGuard';

// Exported for storybook
const accountBillingEmailQuery = gql`
  query UI__accountBillingEmailQuery($accountId: ID!) {
    account(id: $accountId) {
      id
      name
      billingContactEmail
      isOnTrial
      currentSubscription {
        uuid
        plan {
          id
          provider
        }
      }
    }
  }
`;

interface Props {
  accountId: string;
}

export const NoBillingEmailBanner = ({ accountId }: Props) => {
  const { data, loading } = useQuery<
    GraphQLTypes.UI__accountBillingEmailQuery,
    GraphQLTypes.UI__accountBillingEmailQueryVariables
  >(accountBillingEmailQuery, {
    variables: {
      accountId,
    },
    errorPolicy: 'all',
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const account = data?.account;
  // Note: plans with provider NONE and APOLLO_NO_INVOICING are free plans and are not billed
  const isOnFreePlan =
    account?.currentSubscription?.plan?.provider === BillingProvider.NONE ||
    account?.currentSubscription?.plan?.provider ===
      BillingProvider.APOLLO_NO_INVOICING;

  // Hide if we are loading, our account has an email, or org is on free or trial plan
  if (
    loading ||
    !account ||
    account.billingContactEmail ||
    account.isOnTrial ||
    isOnFreePlan
  ) {
    return null;
  }

  // putting this inside the JSX below crashes eslint-plugin-jsx-a11y
  // see https://github.com/evcohen/eslint-plugin-jsx-a11y/issues/399
  const resetBillingEmailLocation = orgRoutes.settings.location({
    orgId: accountId,
    overlay: 'update-billing-email',
  });

  return (
    <AlertBanner status="error">
      <b>{account.name}</b>&nbsp;does not have a billing contact designated.
      Please{' '}
      <PermissionGuard
        accountId={account.id}
        accountPermissions="canUpdateMetadata"
        fallback={<>ask your account admin to add a billing contact.</>}
        childRenderer={() => (
          <>
            add a billing contact now.
            <Button
              className="ml-3"
              size="sm"
              variant="secondary"
              as={Link}
              to={resetBillingEmailLocation}
            >
              Add a billing contact
            </Button>
          </>
        )}
      />
    </AlertBanner>
  );
};
